/**
 * 后端调用的唯一出口。
 *
 * ⚠️ AGENTS.md §0.3：离线时 `fetch` 抛的是 `TypeError`，不是 HTTP 错误。
 * 「401 就跳登录页」那套拦截器在断网时根本不触发，所以这里把「断网」和
 * 「未授权」显式分成两种结果，让调用方无法忽略其中一种。
 */

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; kind: 'offline' | 'unauthorized' | 'forbidden' | 'server'; message: string }

// 令牌只活在页面内存里：刷新 / 重新打开即重新上锁，与 neo-pwa 的应用锁口径一致。
// 威胁模型是「拿到这台已解锁手机的人」。M3 接入 passkey 后，重新解锁只需刷一次脸。
let token: string | null = null
let role: 'carson' | 'neo' | null = null

export const session = {
  get token() { return token },
  get role() { return role },
  get authed() { return token !== null },
  clear() { token = null; role = null },
}

async function call<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  let res: Response
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    })
  } catch {
    return { ok: false, kind: 'offline', message: '现在连不上，等有网了再试' }
  }
  if (res.status === 401) {
    session.clear()
    return { ok: false, kind: 'unauthorized', message: '需要重新解锁' }
  }
  if (res.status === 403) return { ok: false, kind: 'forbidden', message: '这个角色看不了这一页' }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    return { ok: false, kind: 'server', message: body?.error ?? `出错了（${res.status}）` }
  }
  return { ok: true, data: (await res.json()) as T }
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    call<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),

  async unlock(password: string): Promise<ApiResult<{ role: 'carson' | 'neo' }>> {
    const r = await call<{ token: string; role: 'carson' | 'neo' }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    })
    if (!r.ok) {
      // 密码错走的是 401，但这里不是「会话过期」而是「这次输错了」，措辞要分开
      if (r.kind === 'unauthorized') return { ok: false, kind: 'unauthorized', message: '密码不对' }
      return r
    }
    token = r.data.token
    role = r.data.role
    return { ok: true, data: { role: r.data.role } }
  },
}

export interface NavTab { key: string; label: string; ready: boolean }
